import React, { memo, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { MARKDOWN_COMPONENTS } from "./markdownContext";
import rehypeWordFade from "./rehypeWordFade";

/* Block-split markdown renderer (Wave 3.1, stage 3).
   react-markdown 8 has zero internal memoization: ONE big <ReactMarkdown>
   re-parses the entire answer on every stream frame. Splitting the content
   into blank-line-separated blocks and memoizing each block by its string
   means a frame only re-parses the CHANGED block (the streaming tail);
   completed blocks above it are referentially skipped.

   DOM identity: react-markdown 8 renders a bare React.Fragment root, so N
   per-block fragments inside the same parent produce the exact same element
   sequence as one ReactMarkdown over the whole document — as long as the
   splits land on real markdown block boundaries, which the splitter below
   guards for fenced code, loose lists, blockquotes, and indented code.
   Document-wide reference/footnote definitions bypass the split entirely
   (see HAS_REFERENCE_DEFINITION below). */

const REMARK_PLUGINS = [remarkGfm];

/* Word fade-in during streaming (3.6): while a message streams, a rehype
   plugin wraps each word in <span class="facility-ai-word"> so CSS can
   fade newly appended words in. Both arrays are MODULE-LEVEL constants so
   the plugin identity (and therefore react-markdown 8's internal processor
   useMemo) stays stable frame-to-frame — a fresh `[rehypeWordFade]` per
   render would rebuild the processor and re-parse every block per frame.
   When the message completes, isStreaming flips false and the plugin drops
   away: the spans unmount, the final render is plain text, and every block
   re-renders exactly once for that final plain parse (accepted cost). */
const STREAMING_REHYPE_PLUGINS = [rehypeWordFade];
const IDLE_REHYPE_PLUGINS = [];

/* Fences: 0-3 leading spaces, 3+ backticks or tildes. A backtick fence
   opener's info string may not contain backticks (CommonMark 4.5). */
const FENCE_OPEN_PATTERN = /^ {0,3}(`{3,}|~{3,})(.*)$/;
const FENCE_CLOSE_PATTERN = /^ {0,3}(`{3,}|~{3,})[ \t]*$/;
/* Bullet ("- ", "* ", "+ ") or ordered ("1. ", "23) ") markers, 0-3 spaces. */
const LIST_ITEM_PATTERN = /^ {0,3}(?:[-*+]|\d{1,9}[.)])([ \t]|$)/;
/* Blockquote marker line: 0-3 spaces then ">". */
const BLOCKQUOTE_LINE_PATTERN = /^ {0,3}>/;
/* Indented code line: 4+ leading spaces or a tab (CommonMark 4.4). */
const INDENTED_CODE_LINE_PATTERN = /^(?: {4,}|\t)/;

/* Pure, dependency-free splitter: breaks content into blocks on blank
   lines, but ONLY outside open fenced code blocks. While a fence is open
   every line (blank or not) is absorbed into the current block, so an
   unclosed fence keeps the whole streaming tail as ONE block.

   Three continuation guards keep blank-line-separated constructs in ONE
   block so the per-block parse matches the single-document parse (keeping
   more together is always parse-safe; a wrong SPLIT is what changes DOM):
   - lists: a blank line between items (a loose list) or before an indented
     continuation still renders as a single <ul>/<ol>;
   - blockquotes: a blank line followed by another ">" line continues the
     same blockquote instead of starting a second one;
   - indented code: CommonMark 4.4 keeps ONE 4-space/tab-indented code
     block across blank lines while the next non-blank line is indented
     too, so the blank line stays inside the block (one <pre>, not two). */
export const splitMarkdownBlocks = (content) => {
  const lines = String(content ?? "").split("\n");
  const blocks = [];
  let current = [];
  let blockHasListItem = false;
  let blockHasBlockquote = false;
  let blockHasIndentedCode = false;
  let fenceMarker = null;
  let fenceLength = 0;

  const closeBlock = () => {
    if (current.length) {
      blocks.push(current.join("\n"));
      current = [];
    }
    blockHasListItem = false;
    blockHasBlockquote = false;
    blockHasIndentedCode = false;
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (fenceMarker) {
      current.push(line);
      const closeMatch = line.match(FENCE_CLOSE_PATTERN);
      if (
        closeMatch &&
        closeMatch[1][0] === fenceMarker &&
        closeMatch[1].length >= fenceLength
      ) {
        fenceMarker = null;
      }
      continue;
    }

    const openMatch = line.match(FENCE_OPEN_PATTERN);
    if (openMatch && (openMatch[1][0] === "~" || !openMatch[2].includes("`"))) {
      fenceMarker = openMatch[1][0];
      fenceLength = openMatch[1].length;
      current.push(line);
      continue;
    }

    if (!line.trim()) {
      let nextIndex = index + 1;
      while (nextIndex < lines.length && !lines[nextIndex].trim()) {
        nextIndex += 1;
      }
      const nextLine = lines[nextIndex];
      if (nextLine !== undefined) {
        if (
          blockHasListItem &&
          (LIST_ITEM_PATTERN.test(nextLine) || /^[ \t]+\S/.test(nextLine))
        ) {
          current.push(line);
          continue;
        }
        if (blockHasBlockquote && BLOCKQUOTE_LINE_PATTERN.test(nextLine)) {
          current.push(line);
          continue;
        }
        if (blockHasIndentedCode && INDENTED_CODE_LINE_PATTERN.test(nextLine)) {
          current.push(line);
          continue;
        }
      }
      closeBlock();
      continue;
    }

    if (!blockHasListItem && LIST_ITEM_PATTERN.test(line)) {
      blockHasListItem = true;
    }
    if (!blockHasBlockquote && BLOCKQUOTE_LINE_PATTERN.test(line)) {
      blockHasBlockquote = true;
    }
    /* An indented line with no list context is indented code (a list
       item's indented continuation is already covered by the list guard
       above, so it must not set this flag). */
    if (
      !blockHasIndentedCode &&
      !blockHasListItem &&
      INDENTED_CODE_LINE_PATTERN.test(line)
    ) {
      blockHasIndentedCode = true;
    }
    current.push(line);
  }

  closeBlock();
  return blocks;
};

/* Default shallow memo => re-renders only when the block's string content
   changed. Index keys are safe here: transcript edits are append-only
   during streaming (the tail block grows, new blocks are appended), and
   completed messages never change content, so earlier indexes always map
   to identical strings and hit the memo. `rehypePlugins` is always one of
   the two module constants above, so it only breaks the memo when a
   message's streaming flag actually flips — the intended single final
   re-parse. */
const MarkdownBlock = memo(function MarkdownBlock({ block, rehypePlugins }) {
  return (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={rehypePlugins}
      components={MARKDOWN_COMPONENTS}
    >
      {block}
    </ReactMarkdown>
  );
});

/* Reference-style link (`[label]: /url`) and GFM footnote (`[^1]: note`)
   definitions resolve DOCUMENT-WIDE, so a definition in a different block
   than its usage would render literal "[1]" / "[^1]" text in the split
   render. They are rare in chat replies: fall back to ONE whole-document
   ReactMarkdown — exactly the old single-render (HEAD) behavior. False
   positives (definition-looking lines inside fenced code) only downgrade
   to the single render, which is always safe. */
const HAS_REFERENCE_DEFINITION = /^[ \t]{0,3}\[[^\]]+\]:/m;

/* `isStreaming` now drives the word-fade rehype plugin (3.6): true →
   per-word spans on BOTH render paths (per-block and the whole-document
   fallback); false → no rehype plugins, plain text output. No other props
   are forwarded — hidden props would bypass MarkdownBlock's memo. */
const MemoizedMarkdown = ({ content, isStreaming = false }) => {
  const text = String(content ?? "");
  const rehypePlugins = isStreaming ? STREAMING_REHYPE_PLUGINS : IDLE_REHYPE_PLUGINS;
  /* Hooks must run unconditionally (rule-of-hooks), so the definition
     check happens inside the memo and yields `null` blocks on fallback. */
  const blocks = useMemo(
    () => (HAS_REFERENCE_DEFINITION.test(text) ? null : splitMarkdownBlocks(text)),
    [text]
  );

  if (blocks === null) {
    return (
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={rehypePlugins}
        components={MARKDOWN_COMPONENTS}
      >
        {text}
      </ReactMarkdown>
    );
  }

  return (
    <>
      {blocks.map((block, index) => (
        <React.Fragment key={index}>
          {/* Byte-identical output: one ReactMarkdown keeps the "\n" text
              nodes remark emits between top-level blocks; re-insert them so
              the split render produces the exact same markup. */}
          {index > 0 ? "\n" : null}
          <MarkdownBlock block={block} rehypePlugins={rehypePlugins} />
        </React.Fragment>
      ))}
    </>
  );
};

export default MemoizedMarkdown;
